if (!customElements.get('floating-atc')) {
  class FloatingAtc extends HTMLElement {
    connectedCallback() {
      this.mainForm = document.getElementById(this.dataset.formId);
      this.productForm = this.mainForm ? this.mainForm.closest('product-form') : null;
      this.btn = this.querySelector('.js-floating-atc-btn');
      this.priceEl = this.querySelector('.js-floating-atc-price');
      this.prices = JSON.parse(this.querySelector('.js-floating-atc-prices').textContent);
      this.variantId = this.mainForm ? this.mainForm.querySelector('[name="id"]').value : null;

      if (!this.productForm) return;

      // The root reaches far below the viewport, so the form only stops intersecting once it
      // has left through the top. Without it, jumping from above the form to below it (e.g. a
      // skip link or anchor) never changes the intersection state and no callback fires.
      this.observer = new IntersectionObserver(([entry]) => {
        this.pastTrigger = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.toggle();
      }, { rootMargin: '0px 0px 100000px 0px' });
      this.observer.observe(this.productForm);

      this.resizeObserver = new ResizeObserver(() => {
        document.body.style.setProperty('--floating-atc-height', `${this.offsetHeight}px`);
      });
      this.resizeObserver.observe(this);

      this.variantChangeHandler = this.handleVariantChange.bind(this);
      this.purchaseOptionHandler = this.handlePurchaseOption.bind(this);
      this.cartDoneHandler = this.stopLoading.bind(this);
      this.breakpointHandler = this.toggle.bind(this);

      document.addEventListener('on:variant:change', this.variantChangeHandler);
      document.addEventListener('input', this.purchaseOptionHandler);
      ['on:cart:add', 'on:line-item:change', 'on:cart:error'].forEach((evt) => {
        document.addEventListener(evt, this.cartDoneHandler);
      });
      window.addEventListener('on:breakpoint-change', this.breakpointHandler);

      if (this.btn) this.btn.addEventListener('click', this.startLoading.bind(this));
      this.initStars();
      this.addEventListener('focusout', () => setTimeout(() => this.toggle()));
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      document.removeEventListener('on:variant:change', this.variantChangeHandler);
      document.removeEventListener('input', this.purchaseOptionHandler);
      ['on:cart:add', 'on:line-item:change', 'on:cart:error'].forEach((evt) => {
        document.removeEventListener(evt, this.cartDoneHandler);
      });
      window.removeEventListener('on:breakpoint-change', this.breakpointHandler);
      document.body.classList.remove('has-floating-atc');
    }

    /**
     * Yotpo's page scan renders the stars async with no callback. Once they're in, hides them
     * for products with no reviews, and swaps the "Jump to reviews" button (a second tab stop
     * for the same thing) for plain text for screen readers.
     */
    initStars() {
      const stars = this.querySelector('.floating-atc__stars');
      if (!stars) return;

      const tidy = () => {
        if (!stars.querySelector('.yotpo-sr-bottom-line-button')) return false;
        const score = stars.querySelector('.yotpo-sr-bottom-line-score')?.textContent.trim();
        const count = stars.querySelector('.yotpo-sr-bottom-line-text')?.textContent.replace(/\D/g, '');
        stars.inert = true;
        stars.hidden = !score || !count || count === '0';

        if (!stars.hidden) {
          const label = document.createElement('span');
          label.className = 'visually-hidden';
          label.textContent = `Rated ${score} out of 5 stars`;
          stars.after(label);
        }
        return true;
      };

      if (tidy()) return;
      const observer = new MutationObserver(() => {
        if (tidy()) observer.disconnect();
      });
      observer.observe(stars, { childList: true, subtree: true });
    }

    /**
     * Shows the bar once the main add to cart has scrolled above the viewport (mobile only).
     */
    toggle() {
      const show = this.pastTrigger && !theme.mediaMatches.md;

      // Don't pull the bar out from under keyboard focus; hide it once focus leaves
      if (!show && this.contains(document.activeElement)) return;

      this.classList.toggle('is-visible', show);
      this.inert = !show;
      document.body.classList.toggle('has-floating-atc', show);
    }

    /**
     * Mirrors the main form's variant: availability and price.
     * @param {CustomEvent} evt - on:variant:change event.
     */
    handleVariantChange(evt) {
      if (!this.mainForm || evt.detail.form !== this.mainForm) return;

      const { variant } = evt.detail;
      this.variantId = variant ? String(variant.id) : null;

      if (this.btn) {
        const available = Boolean(variant && variant.available);
        this.btn.disabled = !available;
        this.btn.textContent = available
          ? this.btn.dataset.addToCartText
          : (variant ? theme.strings.noStock : theme.strings.noVariant);
      }

      this.updatePrice();
    }

    /**
     * Recharge's purchase options live in a shadow root, but their input events are composed
     * and reach the document retargeted to the widget. It writes the chosen selling plan to
     * the main form in a microtask, so read it after that.
     * @param {Event} evt - input event.
     */
    handlePurchaseOption(evt) {
      if (evt.target.tagName !== 'RECHARGE-SUBSCRIPTION-WIDGET') return;
      setTimeout(() => this.updatePrice());
    }

    /**
     * Shows the one-time price, or the subscription price when a selling plan is selected, so
     * it matches what the cart will show.
     */
    updatePrice() {
      const prices = this.prices[this.variantId];
      if (!prices) return;

      const sellingPlan = this.mainForm.querySelector('[name="selling_plan"]')?.value;
      this.priceEl.innerHTML = (sellingPlan && prices.plans[sellingPlan]) || prices.price;
    }

    startLoading() {
      if (this.btn.disabled) return;
      this.btn.classList.add('is-loading');
      this.loadingTimeout = setTimeout(() => this.stopLoading(), 8000);
    }

    /**
     * @param {CustomEvent} [evt] - Cart event.
     */
    stopLoading(evt) {
      if (!this.btn || !this.btn.classList.contains('is-loading')) return;
      clearTimeout(this.loadingTimeout);
      this.btn.classList.remove('is-loading');

      // The error is shown in the main form, out of view, so take the customer to it
      if (evt && evt.type === 'on:cart:error') {
        const error = this.productForm && this.productForm.querySelector('.js-form-error:not([hidden])');
        if (error) {
          error.setAttribute('tabindex', '-1');
          error.scrollIntoView({ block: 'center' });
          error.focus({ preventScroll: true });
        }
      }
    }
  }

  customElements.define('floating-atc', FloatingAtc);
}
